import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  BadgeCheck,
  LayoutGrid,
  Mail,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
  UserMinus,
  UserPlus,
  UserX,
} from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { FormularioMinisterio } from "@/core/components/FormularioMinisterio";
import { FormularioFuncao } from "@/core/components/FormularioFuncao";
import { useAuth } from "@/core/hooks/useAuth";
import {
  useAdicionarIntegrante,
  useAtualizarFuncao,
  useAtualizarMinisterio,
  useCriarFuncao,
  useDefinirLider,
  useDefinirMinisterioAtivo,
  useExcluirFuncao,
  useExcluirMinisterio,
  useMinisterioDetalhe,
  useRemoverIntegrante,
  useRemoverTodosVinculos,
} from "@/core/hooks/useMinisterios";
import { usePessoasParaConvite } from "@/core/hooks/useConvites";
import { funcaoExigeAceite } from "@/core/utils/funcaoMinisterial";
import {
  CLASSE_SELO_COMUM,
  CLASSE_SELO_GESTOR,
  CLASSE_SELO_GESTOR_CONVIDADO,
  CLASSE_SELO_LIDER,
  CLASSE_SELO_LIDER_CONVIDADO,
  ROTULO_GESTOR_DE_ELO,
  rotuloDoGestor,
} from "@/core/utils/selosFuncao";
import type {
  FuncaoMinisterial,
  IntegranteMinisterio,
} from "@/core/services/ministeriosService";

type Mensagem = { tipo: "sucesso" | "erro"; texto: string } | null;

/**
 * Detalhe de um ministério (Núcleo). Serve DOIS papéis (DA-004 v2 — Skill
 * v3.0, autonomia do líder):
 *   - ADMIN da plataforma (rota /admin/ministerios/:id): tudo — edita dados,
 *     desativa/reativa, define líder, gerencia funções e integrantes
 *     (cadastro direto — DA-003.2 — e convite).
 *   - LÍDER do ministério (rota /ministerios/:id, fora do guard de admin):
 *     define líder e gerencia funções e integrantes por CONVITE (DA-003.1),
 *     inclusive para funções de líder e de gestor — sem intervenção do
 *     admin. Só não edita os dados do ministério nem o desativa.
 * É aqui também que mora a visão "quem gerencia o módulo" (a antiga tela
 * Acessos foi extinta): módulos sob responsabilidade + integrantes com
 * função que gerencia módulo. A RLS é o portão real; aqui só decidimos o
 * que renderizar.
 */
const ROTULO_LIDER_MINISTERIO = "Líder do Ministério";

/**
 * Texto do chip de função de cada integrante. A função "Líder" que o sistema cria em
 * todo ministério (eh_lider) parecia uma função comum do ministério; agora o chip diz
 * "Líder do Ministério". Se a função de liderança tiver outro nome, ele vai junto.
 * A função com o marcador "Gestor de elo" ganha o mesmo tratamento ("Gestor de elo" ou
 * "Gestor de elo · nome"). Se a função for as duas coisas, vale o selo do líder.
 * Só vale para vínculo ativo ou convidado: vínculo inativo mostra o nome da função.
 */
function rotuloDoVinculo(v: {
  funcaoNome: string;
  ehLider: boolean;
  ehGestor?: boolean;
  status: string;
}): string {
  if (v.status !== "ativo" && v.status !== "convidado") return v.funcaoNome;
  if (v.ehLider) {
    return v.funcaoNome.trim().toLowerCase() === "líder"
      ? ROTULO_LIDER_MINISTERIO
      : `${ROTULO_LIDER_MINISTERIO} · ${v.funcaoNome}`;
  }
  return v.ehGestor ? rotuloDoGestor(v.funcaoNome) : v.funcaoNome;
}

export function MinisterioDetalhe() {
  const { id } = useParams<{ id: string }>();
  const { pessoa, ehAdminPlataforma } = useAuth();
  const { data, isLoading, isError } = useMinisterioDetalhe(id);
  const navigate = useNavigate();

  const atualizar = useAtualizarMinisterio();
  const definirAtivo = useDefinirMinisterioAtivo();
  const excluirMinisterio = useExcluirMinisterio();
  const definirLider = useDefinirLider();
  const criarFuncao = useCriarFuncao();
  const atualizarFuncao = useAtualizarFuncao();
  const excluirFuncao = useExcluirFuncao();
  const adicionarIntegrante = useAdicionarIntegrante();
  const removerIntegrante = useRemoverIntegrante();
  const removerTodosVinculos = useRemoverTodosVinculos();

  const [mensagem, setMensagem] = useState<Mensagem>(null);
  const [erroModal, setErroModal] = useState<string | null>(null);

  const [modalEditar, setModalEditar] = useState(false);
  const [confirmandoAtivo, setConfirmandoAtivo] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [modalLider, setModalLider] = useState(false);
  const [liderParaDefinir, setLiderParaDefinir] = useState<{ id: string; nome: string } | null>(null);
  const [pesquisaLider, setPesquisaLider] = useState("");
  const [modalFuncao, setModalFuncao] = useState<{ funcao: FuncaoMinisterial | null } | null>(null);
  const [funcaoParaExcluir, setFuncaoParaExcluir] = useState<FuncaoMinisterial | null>(null);
  const [modalIntegrante, setModalIntegrante] = useState<"direto" | "convite" | null>(null);
  const [funcaoSelecionada, setFuncaoSelecionada] = useState("");
  const [pesquisaPessoa, setPesquisaPessoa] = useState("");
  const [integranteParaRemover, setIntegranteParaRemover] =
    useState<IntegranteMinisterio | null>(null);
  // A39 (reteste 23-24/07/2026): botão geral "remover integrante" — antes só
  // aparecia quando a pessoa tinha 1 função; agora fica sempre visível e, com
  // 2+ funções, remove todas de uma vez (desvincula do ministério por completo).
  const [grupoParaRemover, setGrupoParaRemover] = useState<{
    pessoaId: string;
    nome: string;
    vinculos: IntegranteMinisterio[];
  } | null>(null);
  // Líder dá outra função a quem já é integrante (migration 0112): função comum entra na
  // hora; Líder do Ministério e Gestor de elo seguem por convite e dependem do aceite.
  const [grupoParaDarFuncao, setGrupoParaDarFuncao] = useState<{
    pessoaId: string;
    nome: string;
    vinculos: IntegranteMinisterio[];
  } | null>(null);

  // Candidatas para convite/cadastro (RPC 0024 — funciona para admin E líder).
  // Refiltra por função selecionada: quem já tem OUTRA função no ministério
  // continua elegível para receber esta (uma pessoa pode ter mais de uma função).
  const { data: candidatas } = usePessoasParaConvite(
    id,
    modalIntegrante !== null,
    funcaoSelecionada || undefined,
  );

  const ehLiderDoMinisterio = useMemo(
    () =>
      !!pessoa &&
      !!data?.integrantes.some(
        (i) => i.pessoaId === pessoa.id && i.ehLider && i.status === "ativo",
      ),
    [data, pessoa],
  );
  const podeGovernar = ehAdminPlataforma || ehLiderDoMinisterio;

  const lideresAtuais = useMemo(
    () => (data?.integrantes ?? []).filter((i) => i.ehLider && i.status === "ativo"),
    [data],
  );

  // UX-05: agrupa os vínculos por pessoa (uma pessoa pode ter mais de uma
  // função no mesmo ministério) — preserva a ordem de primeira aparição.
  const integrantesAgrupados = useMemo(() => {
    const porPessoa = new Map<
      string,
      { pessoaId: string; nome: string; vinculos: IntegranteMinisterio[] }
    >();
    for (const i of data?.integrantes ?? []) {
      const grupo = porPessoa.get(i.pessoaId);
      if (grupo) {
        grupo.vinculos.push(i);
      } else {
        porPessoa.set(i.pessoaId, { pessoaId: i.pessoaId, nome: i.nome, vinculos: [i] });
      }
    }
    return [...porPessoa.values()];
  }, [data]);

  const candidatasFiltradas = useMemo(() => {
    const termo = pesquisaPessoa.trim().toLowerCase();
    return (candidatas ?? [])
      .filter((p) => !termo || p.nome_completo.toLowerCase().includes(termo))
      .sort((a, b) => a.nome_completo.localeCompare(b.nome_completo));
  }, [candidatas, pesquisaPessoa]);

  // Candidatas a líder: PROMOÇÃO de integrante ativo (uma pessoa 1x, mesmo
  // com várias funções). Quem ainda não é do ministério entra por convite
  // com a função de líder (DA-003) — inclusive convidado pelo próprio líder.
  const candidatasLider = useMemo(() => {
    const termo = pesquisaLider.trim().toLowerCase();
    const liderIds = new Set(lideresAtuais.map((l) => l.pessoaId));
    const porPessoa = new Map<string, { id: string; nome: string }>();
    for (const i of data?.integrantes ?? []) {
      if (i.status !== "ativo" || liderIds.has(i.pessoaId)) continue;
      porPessoa.set(i.pessoaId, { id: i.pessoaId, nome: i.nome });
    }
    return [...porPessoa.values()]
      .filter((p) => !termo || p.nome.toLowerCase().includes(termo))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [data, pesquisaLider, lideresAtuais]);

  if (isLoading) {
    return (
      <main className="mx-auto w-full max-w-4xl px-4 pt-8 sm:px-6">
        <p className="text-sm text-muted">Carregando...</p>
      </main>
    );
  }
  if (isError || !data) {
    return (
      <main className="mx-auto w-full max-w-4xl px-4 pt-8 sm:px-6">
        <p role="alert" className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
          Não foi possível carregar o ministério.
        </p>
      </main>
    );
  }
  // Sem papel de governança aqui (nem admin, nem líder ativo): volta ao hub.
  if (!podeGovernar) {
    return <Navigate to="/" replace />;
  }

  const { ministerio, funcoes, integrantes, modulosResponsavel } = data;

  // Quem gerencia os módulos deste ministério (função com pode_gerenciar_modulo).
  const gestoresDeModulo = [
    ...new Map(
      integrantes
        .filter((i) => i.status === "ativo")
        .filter((i) => funcoes.find((f) => f.id === i.funcaoId)?.pode_gerenciar_modulo)
        .map((i) => [i.pessoaId, i.nome]),
    ).values(),
  ].sort((a, b) => a.localeCompare(b));

  function exibir(tipo: "sucesso" | "erro", texto: string) {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem(null), 3500);
  }

  async function handleEditar(dados: { nome: string; descricao: string | null }) {
    setErroModal(null);
    try {
      await atualizar.mutateAsync({ id: ministerio.id, ...dados });
      setModalEditar(false);
      exibir("sucesso", "Ministério atualizado.");
    } catch (e) {
      setErroModal(e instanceof Error ? e.message : "Não foi possível atualizar.");
    }
  }

  async function handleAtivo() {
    try {
      await definirAtivo.mutateAsync({ id: ministerio.id, ativo: !ministerio.ativo });
      setConfirmandoAtivo(false);
      exibir("sucesso", ministerio.ativo ? "Ministério desativado." : "Ministério reativado.");
    } catch (e) {
      setConfirmandoAtivo(false);
      exibir("erro", e instanceof Error ? e.message : "Não foi possível alterar o status.");
    }
  }

  async function handleExcluir() {
    try {
      await excluirMinisterio.mutateAsync(ministerio.id);
      // Volta para a lista — a página deste ministério deixou de existir.
      navigate("/admin/ministerios", { replace: true });
    } catch (e) {
      setConfirmandoExclusao(false);
      exibir("erro", e instanceof Error ? e.message : "Não foi possível excluir o ministério.");
    }
  }

  async function handleDefinirLider(pessoaId: string, nome: string) {
    try {
      await definirLider.mutateAsync({ ministerioId: ministerio.id, pessoaId });
      setModalLider(false);
      exibir("sucesso", `${nome} agora é líder de ${ministerio.nome}.`);
    } catch (e) {
      exibir("erro", e instanceof Error ? e.message : "Não foi possível definir o líder.");
    } finally {
      setLiderParaDefinir(null);
    }
  }

  async function handleSalvarFuncao(dados: {
    nome: string;
    ehLider: boolean;
    podeGerenciarModulo: boolean;
  }) {
    setErroModal(null);
    try {
      if (modalFuncao?.funcao) {
        await atualizarFuncao.mutateAsync({
          ministerioId: ministerio.id,
          funcaoId: modalFuncao.funcao.id,
          dados,
        });
        exibir("sucesso", "Função atualizada.");
      } else {
        await criarFuncao.mutateAsync({ ministerioId: ministerio.id, dados });
        exibir("sucesso", "Função criada.");
      }
      setModalFuncao(null);
    } catch (e) {
      setErroModal(e instanceof Error ? e.message : "Não foi possível salvar a função.");
    }
  }

  async function handleExcluirFuncao() {
    if (!funcaoParaExcluir) return;
    try {
      await excluirFuncao.mutateAsync({
        ministerioId: ministerio.id,
        funcaoId: funcaoParaExcluir.id,
      });
      setFuncaoParaExcluir(null);
      exibir("sucesso", "Função excluída.");
    } catch (e) {
      setFuncaoParaExcluir(null);
      exibir("erro", e instanceof Error ? e.message : "Não foi possível excluir a função.");
    }
  }

  async function handleAdicionar(pessoaId: string, nome: string) {
    if (!funcaoSelecionada || !modalIntegrante) return;
    const convite = modalIntegrante === "convite";
    try {
      await adicionarIntegrante.mutateAsync({
        pessoaId,
        ministerioId: ministerio.id,
        funcaoId: funcaoSelecionada,
        status: convite ? "convidado" : "ativo",
      });
      setModalIntegrante(null);
      exibir(
        "sucesso",
        convite
          ? `Convite enviado para ${nome} — aparece para a pessoa no Início e no sino, e chega como aviso no celular.`
          : `${nome} agora é integrante de ${ministerio.nome}.`,
      );
    } catch (e) {
      exibir("erro", e instanceof Error ? e.message : "Não foi possível adicionar.");
    }
  }

  async function handleDarFuncao(funcao: FuncaoMinisterial) {
    if (!grupoParaDarFuncao) return;
    const { pessoaId, nome } = grupoParaDarFuncao;
    const exigeAceite = funcaoExigeAceite(funcao);
    const rotulo = rotuloDoVinculo({
      funcaoNome: funcao.nome,
      ehLider: funcao.eh_lider,
      ehGestor: funcao.pode_gerenciar_modulo,
      status: "ativo",
    });
    try {
      await adicionarIntegrante.mutateAsync({
        pessoaId,
        ministerioId: ministerio.id,
        funcaoId: funcao.id,
        status: exigeAceite ? "convidado" : "ativo",
      });
      setGrupoParaDarFuncao(null);
      exibir(
        "sucesso",
        exigeAceite
          ? `Convite enviado para ${nome} (${rotulo}). A pessoa precisa aceitar.`
          : `${nome} agora tem a função ${rotulo}. A pessoa foi avisada.`,
      );
    } catch (e) {
      setGrupoParaDarFuncao(null);
      exibir("erro", e instanceof Error ? e.message : "Não foi possível dar a função.");
    }
  }

  async function handleRemover() {
    if (!integranteParaRemover) return;
    const { vinculoId, nome, status } = integranteParaRemover;
    try {
      await removerIntegrante.mutateAsync({ ministerioId: ministerio.id, vinculoId });
      setIntegranteParaRemover(null);
      exibir("sucesso", status === "convidado" ? "Convite cancelado." : `${nome} foi removido(a).`);
    } catch (e) {
      setIntegranteParaRemover(null);
      exibir("erro", e instanceof Error ? e.message : "Não foi possível remover.");
    }
  }

  /** A39: remove TODAS as funções do grupo de uma vez, desvinculando do ministério. */
  async function handleRemoverGrupo() {
    if (!grupoParaRemover) return;
    const { pessoaId, nome } = grupoParaRemover;
    try {
      await removerTodosVinculos.mutateAsync({ ministerioId: ministerio.id, pessoaId });
      setGrupoParaRemover(null);
      exibir("sucesso", `${nome} foi removido(a) do ministério.`);
    } catch (e) {
      setGrupoParaRemover(null);
      exibir("erro", e instanceof Error ? e.message : "Não foi possível remover.");
    }
  }

  function abrirModalIntegrante(modo: "direto" | "convite") {
    setPesquisaPessoa("");
    setFuncaoSelecionada(funcoes.find((f) => !f.eh_lider)?.id ?? funcoes[0]?.id ?? "");
    setModalIntegrante(modo);
  }

  const chipFlags = (f: { eh_lider: boolean; pode_gerenciar_modulo: boolean }) => (
    <span className="flex flex-wrap gap-1">
      {f.eh_lider && (
        <span
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${CLASSE_SELO_LIDER}`}
        >
          <span aria-hidden="true" style={{ fontSize: 10, lineHeight: 1 }}>🗣️</span> {ROTULO_LIDER_MINISTERIO}
        </span>
      )}
      {f.pode_gerenciar_modulo && (
        <span
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${CLASSE_SELO_GESTOR}`}
        >
          <BadgeCheck size={10} aria-hidden="true" /> {ROTULO_GESTOR_DE_ELO}
        </span>
      )}
    </span>
  );

  return (
    <>
      <main className="mx-auto w-full max-w-4xl space-y-6 px-4 pb-12 pt-8 sm:px-6 sm:pt-10">
        {mensagem && (
          <p
            role="alert"
            className={`rounded-xl px-4 py-3 text-sm font-medium ${
              mensagem.tipo === "sucesso" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"
            }`}
          >
            {mensagem.texto}
          </p>
        )}

        {/* --- Dados do ministério ------------------------------------------ */}
        <section className="rounded-2xl border border-border bg-surface p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-black tracking-tight text-primary">
                  {ministerio.nome}
                </h1>
                {!ministerio.ativo && (
                  <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold text-muted">
                    Inativo
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-muted">
                {ministerio.descricao || "Sem descrição."}
              </p>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-text">
                <span aria-hidden="true" className="shrink-0" style={{ fontSize: 14, lineHeight: 1 }}>🗣️</span>
                {lideresAtuais.length
                  ? lideresAtuais.map((l) => l.nome).join(", ")
                  : "Sem líder definido."}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {/* Trocar/definir líder é exclusivo do Admin da Plataforma — o próprio
                  líder não pode se substituir nem substituir outro líder (evita
                  autopromoção/expulsão indevida de liderança). */}
              {ehAdminPlataforma && (
                <button
                  onClick={() => {
                    setPesquisaLider("");
                    setModalLider(true);
                  }}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-on-primary transition-colors hover:bg-primary-hover"
                >
                  <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>🗣️</span>
                  {lideresAtuais.length ? "Trocar líder" : "Definir líder"}
                </button>
              )}
              {ehAdminPlataforma && (
                <>
                  <button
                    onClick={() => {
                      setErroModal(null);
                      setModalEditar(true);
                    }}
                    className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-bold text-text transition-colors hover:border-primary hover:text-primary"
                  >
                    <Pencil size={14} />
                    Editar
                  </button>
                  <button
                    onClick={() => setConfirmandoAtivo(true)}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${
                      ministerio.ativo
                        ? "border-danger/40 text-danger hover:bg-danger/10"
                        : "border-success/40 text-success hover:bg-success/10"
                    }`}
                  >
                    <Power size={14} />
                    {ministerio.ativo ? "Desativar" : "Reativar"}
                  </button>
                  {/* Excluir de vez: só sem módulo vinculado (o service e o FK garantem). */}
                  {modulosResponsavel.length === 0 && (
                    <button
                      onClick={() => setConfirmandoExclusao(true)}
                      className="flex items-center gap-1.5 rounded-lg border border-danger/40 px-3 py-2 text-xs font-bold text-danger transition-colors hover:bg-danger/10"
                    >
                      <Trash2 size={14} />
                      Excluir
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* --- Módulos sob responsabilidade (ex-tela Acessos, Skill v3.0) ---- */}
        {modulosResponsavel.length > 0 && (
          <section className="rounded-2xl border border-border bg-surface p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                <LayoutGrid size={20} />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-bold text-text">
                  {modulosResponsavel.length === 1
                    ? "Elo sob responsabilidade"
                    : "Elos sob responsabilidade"}
                </h2>
                <p className="mt-0.5 flex flex-wrap gap-1 text-xs text-muted">
                  {modulosResponsavel.map((m) => (
                    <span
                      key={m.codigo}
                      className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary"
                    >
                      {m.nome}
                    </span>
                  ))}
                </p>
                <p className="mt-2 text-sm text-text">
                  {gestoresDeModulo.length ? (
                    <>
                      <span className="text-muted">Quem gerencia: </span>
                      {gestoresDeModulo.join(", ")}
                    </>
                  ) : (
                    <span className="text-muted">
                      Ninguém gerencia ainda — adicione um integrante com uma função que tenha o
                      marcador "Gestor de elo".
                    </span>
                  )}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* --- Funções (DA-001) --------------------------------------------- */}
        <section className="overflow-hidden rounded-2xl border border-border bg-surface">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-4 sm:px-5">
            <div>
              <h2 className="text-base font-bold text-text">Funções ministeriais</h2>
              <p className="mt-0.5 text-xs text-muted">
                Funções deste ministério. Dois marcadores dão liderança e gestão de elo.
              </p>
            </div>
            <button
              onClick={() => {
                setErroModal(null);
                setModalFuncao({ funcao: null });
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-on-primary transition-colors hover:bg-primary-hover"
            >
              + Nova função
            </button>
          </div>
          {!funcoes.length ? (
            <p className="px-4 py-4 text-sm text-muted sm:px-5">Nenhuma função criada ainda.</p>
          ) : (
            <ul>
              {funcoes.map((f, idx) => (
                <li
                  key={f.id}
                  className={`flex items-center gap-3 px-4 py-3 sm:px-5 ${
                    idx > 0 ? "border-t border-border" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-text">{f.nome}</p>
                    <div className="mt-1">{chipFlags(f)}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => {
                        setErroModal(null);
                        setModalFuncao({ funcao: f });
                      }}
                      aria-label={`Editar função ${f.nome}`}
                      className="rounded-lg p-2 text-info transition-colors hover:bg-info/10"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setFuncaoParaExcluir(f)}
                      aria-label={`Excluir função ${f.nome}`}
                      className="rounded-lg p-2 text-danger transition-colors hover:bg-danger/10"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* --- Integrantes (DA-003) ------------------------------------------ */}
        <section className="overflow-hidden rounded-2xl border border-border bg-surface">
          <div className="flex flex-col gap-3 border-b border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h2 className="text-base font-bold text-text">Integrantes</h2>
              <p className="mt-0.5 text-xs text-muted">
                Convite: a pessoa aceita ou recusa.{" "}
                {ehAdminPlataforma && "Cadastro direto entra sem precisar aceitar."}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button
                onClick={() => abrirModalIntegrante("convite")}
                disabled={!funcoes.length}
                className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-on-primary transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Mail size={14} />
                Convidar
              </button>
              {ehAdminPlataforma && (
                <button
                  onClick={() => abrirModalIntegrante("direto")}
                  disabled={!funcoes.length}
                  className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-bold text-text transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <UserPlus size={14} />
                  Adicionar direto
                </button>
              )}
            </div>
          </div>
          {!integrantes.length ? (
            <p className="px-4 py-4 text-sm text-muted sm:px-5">
              Nenhum integrante ainda.{" "}
              {funcoes.length
                ? "Convide alguém ou cadastre diretamente."
                : "Crie uma função antes de adicionar integrantes."}
            </p>
          ) : (
            <ul>
              {/*
                UX-05: uma pessoa pode ter mais de uma função no mesmo
                ministério (ex.: Iluminação + Projeção) — antes cada vínculo
                virava uma linha própria ("João · Iluminação" e "João ·
                Projeção" separados), parecendo cadastro duplicado. Agrupa
                por pessoa; quem tem só uma função continua exatamente como
                antes (uma linha, um botão de remover).
              */}
              {integrantesAgrupados.map((grupo, idx) => (
                <li
                  key={grupo.pessoaId}
                  className={`flex items-center gap-3 px-4 py-3 sm:px-5 ${
                    idx > 0 ? "border-t border-border" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-text">
                      <span className="truncate">{grupo.nome}</span>
                      {grupo.vinculos.some((v) => v.status === "convidado") && (
                        <span className="rounded-full bg-warning/10 px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                          Convite pendente
                        </span>
                      )}
                    </p>
                    {/* A39 (reteste 22-23/07/2026): com 2+ funções, cada chip tem um
                        botão de remover VISÍVEL (antes era um ✕ de 10px quase
                        invisível, e o usuário achava que o botão sumia). */}
                    <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                      {grupo.vinculos.map((v) => (
                        <span
                          key={v.vinculoId}
                          className={`inline-flex items-center gap-1 rounded-full py-0.5 pl-2 text-[10px] font-semibold ${
                            v.ehLider && v.status === "ativo"
                              ? CLASSE_SELO_LIDER
                              : v.ehLider && v.status === "convidado"
                                ? CLASSE_SELO_LIDER_CONVIDADO
                                : v.ehGestor && v.status === "ativo"
                                  ? CLASSE_SELO_GESTOR
                                  : v.ehGestor && v.status === "convidado"
                                    ? CLASSE_SELO_GESTOR_CONVIDADO
                                    : CLASSE_SELO_COMUM
                          } ${grupo.vinculos.length > 1 ? "pr-1" : "pr-2"}`}
                        >
                          {/* UX-LID (reteste): coroa só para líder ATIVO (antes um vínculo
                              de líder inativo ainda aparecia com a coroa — "líder fantasma").
                              O Gestor de elo ativo tem o seu próprio desenho (BadgeCheck), em azul. */}
                          {v.ehLider && v.status === "ativo" && (
                            <span aria-hidden="true" style={{ fontSize: 10, lineHeight: 1 }}>🗣️</span>
                          )}
                          {!v.ehLider && v.ehGestor && v.status === "ativo" && (
                            <BadgeCheck size={10} aria-hidden="true" />
                          )}
                          {rotuloDoVinculo(v)}
                          {grupo.vinculos.length > 1 && (
                            <button
                              onClick={() => setIntegranteParaRemover(v)}
                              aria-label={
                                v.status === "convidado"
                                  ? `Cancelar convite de ${v.nome} para ${v.funcaoNome}`
                                  : `Remover ${v.nome} da função ${v.funcaoNome}`
                              }
                              title={`Remover da função ${v.funcaoNome}`}
                              className="ml-1 grid h-[18px] w-[18px] place-items-center rounded-full bg-danger/15 text-danger transition-colors hover:bg-danger/30"
                            >
                              <UserMinus size={11} />
                            </button>
                          )}
                        </span>
                      ))}
                      {/* Só para quem já é integrante ativo e ainda não tem todas as funções. */}
                      {grupo.vinculos.some((v) => v.status === "ativo") &&
                        funcoes.some((f) => !grupo.vinculos.some((v) => v.funcaoId === f.id)) && (
                          <button
                            type="button"
                            onClick={() => setGrupoParaDarFuncao(grupo)}
                            aria-label={`Dar outra função a ${grupo.nome}`}
                            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-primary px-2 py-0.5 text-[10px] font-bold text-primary transition-colors hover:bg-primary/10"
                          >
                            <Plus size={11} aria-hidden="true" />
                            Função
                          </button>
                        )}
                    </p>
                  </div>
                  {/* A39 (reteste 23-24/07/2026): botão geral de remover agora
                      SEMPRE visível, mesmo com 2+ funções (antes só aparecia
                      com 1 função — com 2+, só dava pra remover função por
                      função, sem jeito de desvincular a pessoa de uma vez). Com
                      1 função remove aquele vínculo direto; com 2+ abre a
                      confirmação de remover todas de uma vez (UserX). */}
                  {grupo.vinculos.length === 1 && grupo.vinculos[0] ? (
                    <button
                      onClick={() => setIntegranteParaRemover(grupo.vinculos[0]!)}
                      aria-label={
                        grupo.vinculos[0].status === "convidado"
                          ? `Cancelar convite de ${grupo.nome}`
                          : `Remover ${grupo.nome}`
                      }
                      className="flex shrink-0 items-center rounded-lg p-2 text-danger transition-colors hover:bg-danger/10"
                    >
                      <UserMinus size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={() => setGrupoParaRemover(grupo)}
                      aria-label={`Remover ${grupo.nome} do ministério (todas as funções)`}
                      title="Remover do ministério (todas as funções)"
                      className="flex shrink-0 items-center rounded-lg p-2 text-danger transition-colors hover:bg-danger/10"
                    >
                      <UserX size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {/* --- Modais --------------------------------------------------------- */}
      {modalEditar && (
        <Modal titulo="Editar ministério" aberto={modalEditar} onClose={() => setModalEditar(false)} carregando={atualizar.isPending}>
          <FormularioMinisterio
            valoresIniciais={{ nome: ministerio.nome, descricao: ministerio.descricao }}
            carregando={atualizar.isPending}
            erro={erroModal}
            onSubmit={handleEditar}
          />
        </Modal>
      )}

      {modalFuncao && (
        <Modal
          titulo={modalFuncao.funcao ? "Editar função" : "Nova função"}
          aberto={!!modalFuncao}
          onClose={() => setModalFuncao(null)}
          carregando={criarFuncao.isPending || atualizarFuncao.isPending}
        >
          <FormularioFuncao
            funcaoEmEdicao={modalFuncao.funcao}
            carregando={criarFuncao.isPending || atualizarFuncao.isPending}
            erro={erroModal}
            onSubmit={handleSalvarFuncao}
          />
        </Modal>
      )}

      {modalLider && (
        <Modal titulo="Definir líder" aberto={modalLider} onClose={() => setModalLider(false)}>
          <p className="text-sm text-muted">
            Um <span className="font-semibold text-text">integrante ativo</span> é promovido a
            líder; o líder atual (se houver) deixa a liderança. Para trazer alguém de fora como
            líder, use "Convidar" com a função de líder. O histórico é preservado.
          </p>
          <div className="relative mt-4">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              type="search"
              autoFocus
              placeholder="Pesquisar integrante..."
              value={pesquisaLider}
              onChange={(e) => setPesquisaLider(e.target.value)}
              className="w-full rounded-xl border border-border bg-surface py-2.5 pl-10 pr-4 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-border">
            {!candidatasLider.length ? (
              <p className="p-4 text-sm text-muted">
                {pesquisaLider
                  ? "Nenhum integrante encontrado."
                  : "Nenhum integrante ativo disponível para promover."}
              </p>
            ) : (
              <ul>
                {candidatasLider.map((p, idx) => (
                  <li
                    key={p.id}
                    className={`flex items-center gap-3 px-3 py-2.5 ${
                      idx > 0 ? "border-t border-border" : ""
                    }`}
                  >
                    <p className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                      {p.nome}
                    </p>
                    <button
                      onClick={() => setLiderParaDefinir({ id: p.id, nome: p.nome })}
                      disabled={definirLider.isPending}
                      className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-60"
                    >
                      Definir
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}

      <ModalConfirmacao
        aberto={!!liderParaDefinir}
        titulo="Definir líder"
        mensagem={`Definir "${liderParaDefinir?.nome ?? ""}" como líder de "${ministerio.nome}"? O líder atual (se houver) deixa a liderança. O histórico é preservado.`}
        textoBotaoConfirmar="Definir líder"
        variante="padrao"
        carregando={definirLider.isPending}
        onConfirmar={() => {
          if (liderParaDefinir) void handleDefinirLider(liderParaDefinir.id, liderParaDefinir.nome);
        }}
        onCancelar={() => setLiderParaDefinir(null)}
      />

      {modalIntegrante && (
        <Modal
          titulo={modalIntegrante === "convite" ? "Convidar pessoa" : "Adicionar integrante"}
          aberto={!!modalIntegrante}
          onClose={() => setModalIntegrante(null)}
        >
          <p className="text-sm text-muted">
            {modalIntegrante === "convite"
              ? "A pessoa recebe o convite e decide aceitar ou recusar."
              : "A pessoa entra imediatamente no ministério, sem precisar aceitar."}
          </p>
          <div className="mt-4 flex flex-col gap-1.5">
            <label htmlFor="funcao-integrante" className="text-sm font-medium text-text">
              Função no ministério
            </label>
            <select
              id="funcao-integrante"
              value={funcaoSelecionada}
              onChange={(e) => setFuncaoSelecionada(e.target.value)}
              className="rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {funcoes.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                  {f.eh_lider ? " (líder do ministério)" : ""}
                  {f.pode_gerenciar_modulo ? " (gestor de elo)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="relative mt-4">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              type="search"
              autoFocus
              placeholder="Pesquisar pessoa ativa..."
              value={pesquisaPessoa}
              onChange={(e) => setPesquisaPessoa(e.target.value)}
              className="w-full rounded-xl border border-border bg-surface py-2.5 pl-10 pr-4 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-border">
            {!candidatasFiltradas.length ? (
              <p className="p-4 text-sm text-muted">
                {pesquisaPessoa
                  ? "Nenhuma pessoa encontrada."
                  : "Nenhuma pessoa disponível (quem já é integrante ou tem convite pendente não aparece)."}
              </p>
            ) : (
              <ul>
                {candidatasFiltradas.map((p, idx) => (
                  <li
                    key={p.id}
                    className={`flex items-center gap-3 px-3 py-2.5 ${
                      idx > 0 ? "border-t border-border" : ""
                    }`}
                  >
                    <p className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                      {p.nome_completo}
                    </p>
                    <button
                      onClick={() => handleAdicionar(p.id, p.nome_completo)}
                      disabled={adicionarIntegrante.isPending || !funcaoSelecionada}
                      className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-60"
                    >
                      {modalIntegrante === "convite" ? "Convidar" : "Adicionar"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}

      {grupoParaDarFuncao && (
        <Modal
          titulo={`Dar função a ${grupoParaDarFuncao.nome}`}
          aberto
          onClose={() => setGrupoParaDarFuncao(null)}
        >
          <p className="text-sm text-muted">
            Já é integrante de {ministerio.nome}, então funções comuns entram na hora e a pessoa é
            avisada. Líder do Ministério e Gestor de elo pedem o aceite dela.
          </p>
          <ul className="mt-4 flex flex-col gap-2">
            {funcoes
              .filter((f) => !grupoParaDarFuncao.vinculos.some((v) => v.funcaoId === f.id))
              .map((f) => {
                const exigeAceite = funcaoExigeAceite(f);
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => handleDarFuncao(f)}
                      disabled={adicionarIntegrante.isPending}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-surface px-3 py-2.5 text-left transition-colors hover:border-primary disabled:opacity-60"
                    >
                      <span className="min-w-0 text-sm font-semibold text-text">
                        {rotuloDoVinculo({
                          funcaoNome: f.nome,
                          ehLider: f.eh_lider,
                          ehGestor: f.pode_gerenciar_modulo,
                          status: "ativo",
                        })}
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          exigeAceite ? "bg-warning/10 text-warning" : "bg-primary/10 text-primary"
                        }`}
                      >
                        {exigeAceite ? "Pede aceite" : "Entra na hora"}
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </Modal>
      )}

      {confirmandoAtivo && (
        <ModalConfirmacao
          aberto={confirmandoAtivo}
          titulo={ministerio.ativo ? "Desativar ministério" : "Reativar ministério"}
          mensagem={
            ministerio.ativo
              ? `Desativar "${ministerio.nome}"? Funções, integrantes e histórico são preservados; elos sob responsabilidade dele podem ficar sem gestão.`
              : `Reativar "${ministerio.nome}"? Ele volta a operar com as funções e integrantes que já tinha.`
          }
          textoBotaoConfirmar={ministerio.ativo ? "Desativar" : "Reativar"}
          variante={ministerio.ativo ? "perigo" : "padrao"}
          carregando={definirAtivo.isPending}
          onConfirmar={handleAtivo}
          onCancelar={() => setConfirmandoAtivo(false)}
        />
      )}

      {confirmandoExclusao && (
        <ModalConfirmacao
          aberto={confirmandoExclusao}
          titulo="Excluir ministério"
          mensagem={`Excluir "${ministerio.nome}" DE VEZ? Todas as funções, integrantes e convites deste ministério são apagados permanentemente — diferente de desativar, não há como voltar atrás. Só é possível porque ele não é responsável por nenhum módulo.`}
          textoBotaoConfirmar="Excluir de vez"
          variante="perigo"
          carregando={excluirMinisterio.isPending}
          onConfirmar={handleExcluir}
          onCancelar={() => setConfirmandoExclusao(false)}
        />
      )}

      {funcaoParaExcluir && (
        <ModalConfirmacao
          aberto={!!funcaoParaExcluir}
          titulo="Excluir função"
          mensagem={`Excluir a função "${funcaoParaExcluir.nome}"? Só é possível se ela nunca foi usada por um integrante — senão, renomeie-a.`}
          textoBotaoConfirmar="Excluir"
          variante="perigo"
          carregando={excluirFuncao.isPending}
          onConfirmar={handleExcluirFuncao}
          onCancelar={() => setFuncaoParaExcluir(null)}
        />
      )}

      {integranteParaRemover && (
        <ModalConfirmacao
          aberto={!!integranteParaRemover}
          titulo={
            integranteParaRemover.status === "convidado" ? "Cancelar convite" : "Remover integrante"
          }
          mensagem={
            integranteParaRemover.status === "convidado"
              ? `Cancelar o convite de ${integranteParaRemover.nome} para "${integranteParaRemover.funcaoNome}"?`
              : `Remover ${integranteParaRemover.nome} da função "${integranteParaRemover.funcaoNome}"?` +
                (integranteParaRemover.ehLider
                  ? " Atenção: essa pessoa perde a liderança do ministério."
                  : "") +
                " O vínculo fica inativo (o histórico é preservado)."
          }
          textoBotaoConfirmar={
            integranteParaRemover.status === "convidado" ? "Cancelar convite" : "Remover"
          }
          variante="perigo"
          carregando={removerIntegrante.isPending}
          onConfirmar={handleRemover}
          onCancelar={() => setIntegranteParaRemover(null)}
        />
      )}

      {grupoParaRemover && (
        <ModalConfirmacao
          aberto={!!grupoParaRemover}
          titulo="Remover integrante"
          mensagem={
            `Remover ${grupoParaRemover.nome} do ministério? Isso remove de uma vez as ${grupoParaRemover.vinculos.length} funções dela (${grupoParaRemover.vinculos.map((v) => v.funcaoNome).join(", ")}) e desvincula a pessoa do ministério.` +
            (grupoParaRemover.vinculos.some((v) => v.ehLider && v.status === "ativo")
              ? " Atenção: essa pessoa perde a liderança do ministério."
              : "") +
            " O vínculo fica inativo (o histórico é preservado)."
          }
          textoBotaoConfirmar="Remover"
          variante="perigo"
          carregando={removerTodosVinculos.isPending}
          onConfirmar={handleRemoverGrupo}
          onCancelar={() => setGrupoParaRemover(null)}
        />
      )}
    </>
  );
}
